import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "../lib/api";
import { Calendar, Clock, ChevronDown, Pencil, Check, X, ClipboardList, MapPin, ExternalLink, Film, Info } from "lucide-react";
import { toast } from "sonner";
import { templateHasWatchProviders } from "@/lib/watchProviders";
import { WatchMatchModal } from "@/components/WatchMatchModal";
import { HighlightsModal } from "@/components/HighlightsModal";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { nationalFlag } from "@/lib/flags";
import { conferenceEligibleTeamIds } from "@/lib/conferencePool";
import { openExternalLink } from "@/lib/externalLink";

/**
 * Schedule widget — read-only list of past + live + upcoming games.
 *
 * Shown on LeagueDashboard below the bracket / recent matches section. Data
 * comes from GET /api/tournaments/{tid}/schedule. TBD games render with a
 * round label, scheduled kickoff time, and ESPN bracket placeholder when
 * available.
 */
const PRESETS = [
    { key: "24h", label: "Next 24h", days: 1 },
    { key: "7d", label: "Next 7 days", days: 7 },
    { key: "14d", label: "Next 14 days", days: 14 },
    { key: "all", label: "Full tournament", days: null },
    { key: "past", label: "Past games", days: null, past: true },
];

const _toISO = (d) => d.toISOString().slice(0, 10);

const _dayKey = (iso) => {
    if (!iso) return "TBA";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            weekday: "long", month: "short", day: "numeric", year: "numeric",
        });
    } catch { return iso; }
};

const _timeLabel = (iso) => {
    if (!iso) return "TBA";
    try {
        return new Date(iso).toLocaleTimeString(undefined, {
            hour: "numeric", minute: "2-digit",
        });
    } catch { return ""; }
};

export const ScheduleWidget = ({ tournamentId, tournament, isAdmin, leagueId, teams, onTeamRenamed, template, isSpectator, myTeamIds = [] }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [preset, setPreset] = useState("7d");
    // iter63z'''''''''' — Collapse long schedules. Default to first 10
    // entries, with a "Show all (X more)" button to reveal the rest.
    const [showAll, setShowAll] = useState(false);
    const COLLAPSE_THRESHOLD = 10;

    // iter63z''''''''''' — "Match box score" modal state. Only enabled on
    // logged-in views for supported templates (currently the 3 World Cup
    // templates). Spectator views skip entirely per design.
    const watchEnabled = !isSpectator && templateHasWatchProviders(template);
    const [watchEntry, setWatchEntry] = useState(null);
    // iter87.346 (user-requested) — "Highlights" modal state, shown
    // once a Final MLB game's clip is fetched on demand.
    const [highlightsEntry, setHighlightsEntry] = useState(null);

    const range = useMemo(() => {
        const p = PRESETS.find(x => x.key === preset);
        if (!p) return {};
        // iter63z (bug-2026-06) — Use the USER'S local midnight as the
        // `from` boundary so today's earlier-kickoff games (live or
        // recently completed) are included regardless of the UTC date
        // rollover. Previously this used the UTC date string which
        // dropped today's games on the floor for timezone-edge cases.
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);  // local 00:00
        if (p.past) {
            const old = new Date(startOfToday);
            old.setDate(old.getDate() - 60);
            return { from: old.toISOString(), to: startOfToday.toISOString().slice(0, 10) };
        }
        if (p.days === null) return {};
        const end = new Date(startOfToday);
        end.setDate(end.getDate() + p.days);
        return {
            from: startOfToday.toISOString(),
            to: end.toISOString().slice(0, 10),
        };
    }, [preset]);

    const load = useCallback(async () => {
        if (!tournamentId) return;
        setLoading(true);
        try {
            const res = await api.getSchedule(tournamentId, range);
            setData(res);
        } catch (e) {
            setData(null);
        }
        setLoading(false);
    }, [tournamentId, range]);

    useEffect(() => { load(); }, [load]);

    // iter60 — Auto-refresh every 75s when at least one entry in the current
    // window is LIVE or scheduled for TODAY. Pauses entirely on stale tabs
    // (browsers throttle setInterval but we also defensively gate on
    // document.hidden) so the app doesn't burn ESPN-cache cycles when nobody
    // is watching. Cheap: a single GET /api/tournaments/{tid}/schedule.
    const hasLiveOrToday = useMemo(() => {
        if (!data?.entries) return false;
        const todayIso = new Date().toISOString().slice(0, 10);
        return data.entries.some(e => {
            if (e.status === "live") return true;
            if (!e.scheduled_at || e.played) return false;
            return (e.scheduled_at || "").slice(0, 10) === todayIso;
        });
    }, [data]);

    useEffect(() => {
        if (!hasLiveOrToday) return undefined;
        const id = setInterval(() => {
            if (typeof document !== "undefined" && document.hidden) return;
            load();
        }, 75_000);
        return () => clearInterval(id);
    }, [hasLiveOrToday, load]);

    // iter63z'''''''''' — Reset the expand state when the preset changes,
    // so switching to "Full tournament" doesn't carry over the previous
    // window's expand state.
    useEffect(() => { setShowAll(false); }, [preset]);

    // Group entries by day
    // iter87.115 — For CFB conference-pool tournaments, filter entries to
    // games where at least one team is in the conference (matches the
    // "SEC games include SEC vs non-SEC" semantic).
    const eligibleTeamIds = useMemo(
        () => conferenceEligibleTeamIds(tournament),
        [tournament],
    );
    const groups = useMemo(() => {
        if (!data?.entries) return [];
        const filtered = eligibleTeamIds
            ? data.entries.filter((e) =>
                eligibleTeamIds.has(e.home_team_id) || eligibleTeamIds.has(e.away_team_id))
            : data.entries;
        const m = new Map();
        for (const e of filtered) {
            const k = _dayKey(e.scheduled_at);
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(e);
        }
        const out = Array.from(m.entries());
        // iter87.358 (user-requested) — "Past games" reads naturally
        // most-recent-first (everything else stays earliest-first);
        // the backend always returns entries ascending by date, so
        // just flip the day-group order for this one preset.
        return preset === "past" ? out.reverse() : out;
    }, [data, eligibleTeamIds, preset]);

    // iter63z'''''''''' — Slice the day-groups so only the first
    // COLLAPSE_THRESHOLD entries render unless the user expands. Counts
    // entries across days so the boundary lands at the 10th game total.
    const { displayGroups, hiddenCount } = useMemo(() => {
        const totalEntries = (data?.entries || []).length;
        if (showAll || totalEntries <= COLLAPSE_THRESHOLD) {
            return { displayGroups: groups, hiddenCount: 0 };
        }
        const out = [];
        let count = 0;
        for (const [day, entries] of groups) {
            const remaining = COLLAPSE_THRESHOLD - count;
            if (remaining <= 0) break;
            if (entries.length <= remaining) {
                out.push([day, entries]);
                count += entries.length;
            } else {
                out.push([day, entries.slice(0, remaining)]);
                count = COLLAPSE_THRESHOLD;
                break;
            }
        }
        return { displayGroups: out, hiddenCount: totalEntries - COLLAPSE_THRESHOLD };
    }, [groups, showAll, data]);

    return (
        <div className="space-y-4" data-testid="schedule-widget">
            {/* iter87.25 — Outer card + "Schedule" header removed;
                Takeover shell already carries the icon + title. */}
            <div className="flex items-center justify-end flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <select
                            data-testid="schedule-preset-select"
                            value={preset}
                            onChange={(e) => setPreset(e.target.value)}
                            className="brutal-input text-xs py-1.5 pr-8 appearance-none cursor-pointer"
                        >
                            {PRESETS.map(p => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-500" />
                    </div>
                </div>
            </div>

            {loading && (
                <p className="text-xs text-zinc-500">Loading schedule…</p>
            )}

            {!loading && groups.length === 0 && (
                <div data-testid="schedule-empty" className="space-y-2">
                    <p className="text-xs text-zinc-500">
                        No games scheduled in this window.
                    </p>
                    {preset !== "all" && (
                        <button
                            type="button"
                            onClick={() => setPreset("all")}
                            className="text-xs text-cyber hover:text-volt underline decoration-dotted underline-offset-2 font-bold"
                            data-testid="schedule-show-full"
                        >
                            Show full tournament →
                        </button>
                    )}
                </div>
            )}

            {!loading && displayGroups.map(([day, entries]) => (
                <div key={day} className="space-y-2">
                    <div className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider border-b border-zinc-800 pb-1">
                        {day}
                    </div>
                    <ul className="space-y-2">
                        {entries.map(e => (
                            <ScheduleRow
                                key={e.id}
                                entry={e}
                                isAdmin={isAdmin}
                                leagueId={leagueId}
                                teams={teams}
                                onTeamRenamed={onTeamRenamed}
                                watchEnabled={watchEnabled}
                                onWatch={setWatchEntry}
                                onHighlights={setHighlightsEntry}
                                myTeamIds={myTeamIds}
                            />
                        ))}
                    </ul>
                </div>
            ))}
            {/* iter63z'''''''''' — Expand / collapse footer when there are
                more than 10 entries in the active preset window. */}
            {!loading && hiddenCount > 0 && (
                <div className="pt-1 text-center">
                    <button
                        type="button"
                        data-testid="schedule-show-more-btn"
                        onClick={() => setShowAll(true)}
                        className="text-xs text-cyber hover:text-volt underline decoration-dotted underline-offset-2 font-bold"
                    >
                        Show all ({hiddenCount} more) →
                    </button>
                </div>
            )}
            {!loading && showAll && (data?.entries || []).length > COLLAPSE_THRESHOLD && (
                <div className="pt-1 text-center">
                    <button
                        type="button"
                        data-testid="schedule-show-less-btn"
                        onClick={() => setShowAll(false)}
                        className="text-xs text-zinc-500 hover:text-cyber underline decoration-dotted underline-offset-2"
                    >
                        Collapse to first {COLLAPSE_THRESHOLD} ↑
                    </button>
                </div>
            )}
            {watchEnabled && (
                <WatchMatchModal
                    open={!!watchEntry}
                    onClose={() => setWatchEntry(null)}
                    template={template}
                    entry={watchEntry}
                />
            )}
            <HighlightsModal
                open={!!highlightsEntry}
                onClose={() => setHighlightsEntry(null)}
                entry={highlightsEntry}
            />
        </div>
    );
};

const ScheduleRow = ({ entry, isAdmin, leagueId, teams, onTeamRenamed, watchEnabled, onWatch, onHighlights, myTeamIds = [] }) => {
    const isLive = entry.status === "live";
    const isPlayed = entry.played;
    // iter87.348 (user-requested) — club soccer's Box Score is resolved
    // lazily on click (ESPN bridge), unlike MLB/NFL's precomputed link.
    const [boxscoreLoading, setBoxscoreLoading] = useState(false);
    const homeWon = entry.winner_id && entry.winner_id === entry.home_team_id;
    const awayWon = entry.winner_id && entry.winner_id === entry.away_team_id;
    // iter87.51 — Yellow left-stripe accent when this fixture involves
    // one of the viewer's assigned teams. Matches the Standings "your
    // team" highlight but placed on the row's leading edge so it stays
    // visible next to the LIVE / FINAL pills on the right.
    const isMyFixture = myTeamIds.length > 0 && (
        myTeamIds.includes(entry.home_team_id)
        || myTeamIds.includes(entry.away_team_id)
    );
    // iter63z''''''''''' — Hide the 📋 button on rows where BOTH teams are
    // still TBD placeholders ("Group A Winner" etc.) — no useful box-score
    // link until the matchup is real. Once one side is real we still show.
    const watchShown = watchEnabled && !(entry.home_is_tbd && entry.away_is_tbd);

    return (
        <li
            className={`flex items-center gap-3 p-2 rounded border ${isMyFixture ? "border-l-4 border-l-volt bg-volt/[0.04]" : ""} ${isLive ? "border-neon bg-neon/5" : isPlayed ? "border-zinc-800 bg-zinc-900/50" : "border-zinc-800"}`}
            data-testid={`schedule-row-${entry.id}`}
            data-my-fixture={isMyFixture ? "true" : undefined}
        >
            <div className="text-[10px] text-zinc-500 w-16 shrink-0">
                <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {_timeLabel(entry.scheduled_at)}
                </div>
                {entry.round_label && (
                    <div className="font-bold uppercase mt-0.5 text-cyber">{entry.round_label}</div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <TeamNameCell
                        rawName={entry.home_label}
                        isTbd={entry.home_is_tbd}
                        won={homeWon}
                        isAdmin={isAdmin}
                        leagueId={leagueId}
                        teams={teams}
                        onSaved={onTeamRenamed}
                        side="home"
                        rowId={entry.id}
                    />
                    <span className="text-xs font-mono text-zinc-400 shrink-0">
                        {entry.score_a ?? "—"}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <TeamNameCell
                        rawName={entry.away_label}
                        isTbd={entry.away_is_tbd}
                        won={awayWon}
                        isAdmin={isAdmin}
                        leagueId={leagueId}
                        teams={teams}
                        onSaved={onTeamRenamed}
                        side="away"
                        rowId={entry.id}
                    />
                    <span className="text-xs font-mono text-zinc-400 shrink-0">
                        {entry.score_b ?? "—"}
                    </span>
                </div>
                {entry.bracket_note && (entry.home_is_tbd || entry.away_is_tbd) && (
                    <p className="text-[10px] text-zinc-500 mt-1 italic" data-testid={`schedule-bracket-note-${entry.id}`}>
                        {entry.bracket_note}
                    </p>
                )}
                {/* iter87.177 — GotSport venue line. Prefer the venue
                    detail URL from GotSport when available; fall back
                    to a Google Maps search on the location name so a
                    tap always opens directions on mobile. */}
                {entry.location && (
                    <a
                        href={entry.location_url
                            || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entry.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                            e.preventDefault();
                            openExternalLink(entry.location_url
                                || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entry.location)}`);
                        }}
                        data-testid={`schedule-location-${entry.id}`}
                        className="mt-1 flex items-center gap-1 text-[10px] text-zinc-400 hover:text-cyber transition-colors truncate"
                        title={`Open ${entry.location} in Maps`}
                    >
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{entry.location}</span>
                    </a>
                )}
            </div>
            <div className="shrink-0 flex items-center gap-2">
                {watchShown && (
                    <button
                        type="button"
                        onClick={() => onWatch?.(entry)}
                        data-testid={`schedule-watch-btn-${entry.id}`}
                        className="text-zinc-500 hover:text-cyber transition-colors"
                        title="Open box score"
                        aria-label="Open box score"
                    >
                        <ClipboardList className="w-4 h-4" />
                    </button>
                )}
                <div className="text-right">
                {isLive && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-neon flex flex-col items-end gap-0.5" data-testid={`schedule-row-live-${entry.id}`}>
                        {/* iter87.356 (user-requested) — stacked onto two
                            lines instead of one wide line, so this status
                            block doesn't squeeze team names off narrow
                            screens. */}
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" /> LIVE</span>
                        {entry.live_detail && (
                            <span className="normal-case font-mono text-zinc-300">{entry.live_detail}</span>
                        )}
                    </span>
                )}
                {!isLive && isPlayed && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">FINAL</span>
                )}
                {!isLive && !isPlayed && entry.schedule_notice && (
                    <span
                        className="text-[9px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1 justify-end"
                        data-testid={`schedule-row-notice-${entry.id}`}
                        title={`${entry.schedule_notice} by the league/vendor`}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        {entry.schedule_notice}
                    </span>
                )}
                {!isLive && !isPlayed && !entry.schedule_notice && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-600">UPCOMING</span>
                )}
                {entry.is_manual_override && (
                    <div className="text-[8px] uppercase text-blaze mt-1">manual</div>
                )}
                </div>
                {/* iter87.356 (user-requested) — Box Score + Highlights
                    collapsed into a single small info icon; clicking it
                    opens a compact popover with the two real actions,
                    instead of two full-width pills on every row.
                    Highlights is hidden outright once confirmed empty
                    (entry.highlights_available === false). */}
                {(() => {
                    // iter87.358 (user-reported — "UCL Box Score shows
                    // for all past games, box score not available") —
                    // a direct `game_link` (MLB/NFL) is always real;
                    // a lazy ESPN-bridged `boxscore_endpoint` (club
                    // soccer) needs an explicit confirmed `true`, same
                    // contract as Highlights below.
                    const showBoxscore = Boolean(entry.game_link)
                        || (Boolean(entry.boxscore_endpoint) && entry.boxscore_available === true);
                    // iter87.357 (user-reported — Highlights pill showed
                    // with no actual clip on basically every game) —
                    // now requires an EXPLICIT confirmed `true` from the
                    // backend (a genuine, network-verified check) rather
                    // than "anything but an explicit false". `undefined`/
                    // `null`/`false` all mean "don't show a dead button".
                    const showHighlights = isPlayed && Boolean(entry.highlights_endpoint) && entry.highlights_available === true;
                    if (!showBoxscore && !showHighlights) return null;
                    return (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    data-testid={`schedule-info-btn-${entry.id}`}
                                    className="w-5 h-5 shrink-0 rounded-full border border-zinc-700 text-zinc-500 hover:text-cyber hover:border-cyber transition-colors flex items-center justify-center"
                                    title="Box score / highlights"
                                    aria-label="Box score and highlights"
                                >
                                    <Info className="w-3 h-3" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-44 p-1.5 bg-zinc-900 border-zinc-700" data-testid={`schedule-info-popover-${entry.id}`}>
                                <div className="flex flex-col gap-1">
                                    {showBoxscore && (
                                        entry.game_link ? (
                                            <a
                                                href={entry.game_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => { e.preventDefault(); openExternalLink(entry.game_link); }}
                                                data-testid={`schedule-boxscore-link-${entry.id}`}
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded border border-cyber/30 bg-cyber/5 text-cyber text-[10px] font-bold uppercase tracking-wider hover:bg-cyber/15 hover:border-cyber transition-colors"
                                            >
                                                Box Score <ExternalLink className="w-3 h-3" />
                                            </a>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (boxscoreLoading) return;
                                                    setBoxscoreLoading(true);
                                                    try {
                                                        const res = await api.fetchRelative(entry.boxscore_endpoint);
                                                        if (res?.link) openExternalLink(res.link);
                                                        else toast.info("Box score not available yet for this game.");
                                                    } catch {
                                                        toast.error("Couldn't load the box score link.");
                                                    } finally {
                                                        setBoxscoreLoading(false);
                                                    }
                                                }}
                                                disabled={boxscoreLoading}
                                                data-testid={`schedule-boxscore-link-${entry.id}`}
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded border border-cyber/30 bg-cyber/5 text-cyber text-[10px] font-bold uppercase tracking-wider hover:bg-cyber/15 hover:border-cyber transition-colors disabled:opacity-50"
                                            >
                                                {boxscoreLoading ? "Loading…" : "Box Score"} <ExternalLink className="w-3 h-3" />
                                            </button>
                                        )
                                    )}
                                    {showHighlights && (
                                        <button
                                            type="button"
                                            onClick={() => onHighlights?.(entry)}
                                            data-testid={`schedule-highlights-btn-${entry.id}`}
                                            className="flex items-center gap-1.5 px-2 py-1.5 rounded border border-blaze/30 bg-blaze/5 text-blaze text-[10px] font-bold uppercase tracking-wider hover:bg-blaze/15 hover:border-blaze transition-colors"
                                        >
                                            Highlights <Film className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    );
                })()}
            </div>
        </li>
    );
};

/**
 * One team name cell on a schedule row. When the row is italic+grey (the
 * ESPN/GotSport-provided name didn't match any roster team) AND the viewer
 * is the league admin, expose a pencil that opens a dropdown to pick the
 * roster team to rename → `rawName`.
 */
const TeamNameCell = ({ rawName, isTbd, won, isAdmin, leagueId, teams, onSaved, side, rowId }) => {
    const [editing, setEditing] = useState(false);
    const [pickedTeamId, setPickedTeamId] = useState("");
    const [saving, setSaving] = useState(false);
    const canEdit = isAdmin && isTbd && leagueId && (teams || []).length > 0;

    const handleSave = async () => {
        if (!pickedTeamId) {
            toast.error("Pick a team to rename.");
            return;
        }
        setSaving(true);
        try {
            const r = await api.renameTeam(leagueId, pickedTeamId, rawName);
            if (r.renamed === false) {
                toast.info("Name unchanged.");
            } else {
                const reboundMsg = r.scheduled_relinked > 0
                    ? ` (re-bound ${r.scheduled_relinked} row${r.scheduled_relinked === 1 ? "" : "s"})`
                    : "";
                toast.success(`Renamed → "${r.new_name}"${reboundMsg}.`);
            }
            setEditing(false);
            setPickedTeamId("");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Rename failed");
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="flex items-center gap-1 flex-1 min-w-0" data-testid={`schedule-rename-editor-${rowId}-${side}`}>
                <span className="text-[10px] text-zinc-500 shrink-0">Rename to "{rawName}":</span>
                <select
                    data-testid={`schedule-rename-select-${rowId}-${side}`}
                    value={pickedTeamId}
                    onChange={e => setPickedTeamId(e.target.value)}
                    className="brutal-input text-xs py-0.5 flex-1 min-w-0"
                    disabled={saving}
                >
                    <option value="">Pick team…</option>
                    {teams.map(t => (
                        <option key={t.id} value={t.id}>{nationalFlag(t.flag) ? `${nationalFlag(t.flag)} ` : ""}{t.name}</option>
                    ))}
                </select>
                <button
                    data-testid={`schedule-rename-confirm-${rowId}-${side}`}
                    onClick={handleSave}
                    disabled={saving || !pickedTeamId}
                    className="text-neon hover:text-white shrink-0 disabled:opacity-40"
                    title="Save rename"
                >
                    <Check className="w-4 h-4" />
                </button>
                <button
                    onClick={() => { setEditing(false); setPickedTeamId(""); }}
                    disabled={saving}
                    className="text-zinc-500 hover:text-white shrink-0"
                    title="Cancel"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <span className="flex items-center gap-1 min-w-0 flex-1">
            <span className={`text-sm font-medium truncate ${isTbd ? "text-zinc-500 italic" : ""} ${won ? "text-blaze font-bold" : ""}`}>
                {rawName}
            </span>
            {canEdit && (
                <button
                    data-testid={`schedule-rename-btn-${rowId}-${side}`}
                    onClick={() => setEditing(true)}
                    className="text-zinc-600 hover:text-blaze shrink-0"
                    title={`This name didn't match any roster team. Click to rename a team → "${rawName}".`}
                >
                    <Pencil className="w-3 h-3" />
                </button>
            )}
        </span>
    );
};

export default ScheduleWidget;
